import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArticlePreview } from "../components/PreviewPane";
import { SplitPane } from "../components/SplitPane";
import { Count, Pill, Thumb } from "../components/ui";
import { opsFetch } from "../lib/api";
import { getPostBySlug } from "../lib/contentAdmin";
import { downloadCsv } from "../lib/exportCsv";

type PostsRes = {
  data: {
    items: Array<{
      id: string;
      slug: string;
      title: string;
      status: string;
      post_type: string;
      locale: string;
      city?: string | null;
      excerpt?: string | null;
      hero_image_url?: string | null;
      topic_key?: string | null;
      updated_at?: string | null;
    }>;
    total: number;
  };
};

export function ContentPage() {
  const [status, setStatus] = useState("");
  const [lane, setLane] = useState("insight");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["ops-posts", status, search],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: "100" });
      if (status) params.set("status", status);
      if (search) params.set("q", search);
      return opsFetch<PostsRes>(`/api/ops/content/posts?${params}`);
    },
  });
  const shown = (query.data?.data.items ?? []).filter((item) =>
    lane === "community" ? item.post_type === "community_question" : lane === "insight" ? item.post_type !== "community_question" : true,
  );
  const slug = picked || shown[0]?.slug || "";
  const detail = useQuery({
    queryKey: ["ops-post-preview", slug],
    enabled: Boolean(slug),
    queryFn: () => getPostBySlug(slug),
  });
  const post = detail.data;

  return (
    <SplitPane>
    <div className="ops-panel">
      <div className="ops-toolbar">
        <Link className="ops-btn ops-btn--primary" to="/content/new">
          New post
        </Link>
        <select
          className="ops-input"
          onChange={(e) => setStatus(e.target.value)}
          value={status}
        >
          <option value="">All statuses</option>
          <option value="publish">publish</option>
          <option value="draft">draft</option>
          <option value="scheduled">scheduled</option>
          <option value="trash">trash</option>
        </select>
        <select className="ops-input" onChange={(e) => setLane(e.target.value)} value={lane}>
          <option value="insight">Insights</option>
          <option value="community">Community Q&A</option>
          <option value="all">All</option>
        </select>
        <input
          className="ops-input"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSearch(q.trim());
          }}
          placeholder="Search slug / title"
          value={q}
        />
        <button className="ops-btn" onClick={() => setSearch(q.trim())} type="button">
          Search
        </button>
        <button
          className="ops-btn"
          onClick={() =>
            downloadCsv(
              "posts.csv",
              ["slug", "title", "type", "status", "city", "topic"],
              shown.map((item) => [item.slug, item.title, item.post_type, item.status, item.city || "", item.topic_key || ""]),
            )
          }
          type="button"
        >
          Export
        </button>
        {query.data ? <Count label="posts" n={shown.length} /> : null}
      </div>
      {query.isLoading ? <p className="ops-muted">Loading…</p> : null}
      {query.isError ? <p className="ops-error">{(query.error as Error).message}</p> : null}
      {query.data ? (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Type</th>
                <th>Locale</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr
                  className={slug === p.slug ? "is-selected" : undefined}
                  key={p.id}
                  onClick={() => setPicked(p.slug)}
                >
                  <td>
                    <div className="ops-row-main">
                      <Thumb src={p.hero_image_url || ""} />
                      <div className="ops-row-copy">
                        <strong>{p.title}</strong>
                        <span className="ops-tags">
                          <Pill value={p.post_type} />
                          {p.city ? <span className="ops-muted">{p.city}</span> : null}
                          {p.topic_key ? <span className="ops-muted">{p.topic_key}</span> : null}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <code>{p.slug}</code>
                  </td>
                  <td><Pill value={p.status} /></td>
                  <td><Pill value={p.post_type} /></td>
                  <td>{p.locale}</td>
                  <td className="ops-muted">
                    {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                  </td>
                  <td>
                    <Link className="ops-btn ops-btn--ghost" to={`/content/${encodeURIComponent(p.slug)}`}>
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
    <ArticlePreview
      item={{
        title: String(post?.title || ""),
        excerpt: String(post?.excerpt || post?.seo_description || ""),
        body: String(post?.body_markdown || ""),
        kind: String(post?.post_type || "guide"),
        city: String(post?.city || ""),
        image: String(post?.hero_image_url || post?.og_image_url || ""),
        tags: [String(post?.topic_key || ""), String(post?.city || "")].filter(Boolean),
      }}
    />
    </SplitPane>
  );
}
